import './StatisticsProFans.sass';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Button, Header, ScrollView, Placeholder } from '$uikit';
import { MetaTags } from '$shared/components';
import { IcFilters28 } from '$assets';
import { useScrollCursor, useTranslator } from '$hooks';
import { CurrencyEnum } from '$store/models';
import { toastActions } from '$store/toast';
import { mainActions } from '$store/main';

import { IncomeType } from './StatisticsPro.types';
import { fetchFansList } from './StatisticsProFans.api';
import {
  FanBreakdown,
  FanOrder,
  FanRowData,
  FansLinksFilter,
  FansSummary as FansSummaryData,
  FanSort,
} from './StatisticsProFans.types';
import { presetToRange } from './utils/dateRange';
import { formatAmount } from './utils/fanCharts';
import { DateRangePreset } from './components/DateRangeSection';
import { CurrencyButton } from './components/CurrencyButton';
import { DateButton } from './components/DateButton';
import { FanRow } from './components/FanRow';
import { FanRowSkeleton } from './components/FanRowSkeleton';
import { FansSummary } from './components/FansSummary';
import { FansSummarySkeleton } from './components/FansSummarySkeleton';
import { FansSortModal } from './components/FansSortModal';
import { FanDetailModal } from './components/FanDetailModal';

const PAGE_SIZE = 30;

const MIN_SKELETON_MS = 1000;

const topTypeOf = (breakdown: FanBreakdown, by: FanSort): IncomeType | null => {
  let top: IncomeType | null = null;
  let max = -1;
  for (const [type, stat] of Object.entries(breakdown)) {
    if (!stat) continue;
    const v = by === 'count' ? stat.count : stat.amount;
    if (v > max) {
      max = v;
      top = type as IncomeType;
    }
  }
  return top;
};

export const StatisticsProFans = () => {
  const t = useTranslator();
  const dispatch = useDispatch();

  const [currency, setCurrency] = useState<CurrencyEnum>(CurrencyEnum.EUR);
  const [preset, setPreset] = useState<DateRangePreset>('30d');
  const [range, setRange] = useState<[number, number]>(() => presetToRange('30d', 0, 0));
  const [sort, setSort] = useState<FanSort>('amount');
  const [order, setOrder] = useState<FanOrder>('desc');
  const [promoLinkIds, setPromoLinkIds] = useState<FansLinksFilter>([]);

  const [fans, setFans] = useState<FanRowData[]>([]);
  const [summary, setSummary] = useState<FansSummaryData | null>(null);
  const [nextFrom, setNextFrom] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const [selectedFanId, setSelectedFanId] = useState<number | null>(null);
  const [sortOpen, setSortOpen] = useState(false);

  const {
    activeId: activeUserId,
    setActiveId: setActiveUserId,
    focusAt,
  } = useScrollCursor({
    itemSelector: '.StatisticsProFans__list [data-cursor-id]',
    revision: fans,
  });

  useEffect(() => {
    let cancelled = false;
    let skeletonTimer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();
    setIsLoading(true);
    setHasError(false);
    fetchFansList({
      from: range[0],
      to: range[1],
      currency,
      sort,
      order,
      limit: PAGE_SIZE,
      promoLinkIds,
    })
      .then((res) => {
        if (cancelled) return;
        dispatch(mainActions.setUsers(res.users));
        setFans(res.fans);
        setSummary(res.summary ?? null);
        setNextFrom(res.nextFrom);
        setLoadedOnce(true);
      })
      .catch((e) => {
        if (!cancelled) {
          setFans([]);
          setSummary(null);
          setNextFrom(null);
          setHasError(true);
          dispatch(toastActions.setToastFail(e?.message || 'Failed to load fans'));
        }
      })
      .finally(() => {
        if (cancelled) return;
        const remaining = MIN_SKELETON_MS - (Date.now() - startedAt);
        if (remaining <= 0) {
          setIsLoading(false);
        } else {
          skeletonTimer = setTimeout(() => setIsLoading(false), remaining);
        }
      });
    return () => {
      cancelled = true;
      clearTimeout(skeletonTimer);
    };
  }, [range, currency, sort, order, promoLinkIds, reloadKey, dispatch]);

  const loadingMoreRef = useRef(false);
  const handleLoadMore = useCallback(() => {
    if (loadingMoreRef.current || nextFrom === null) return;
    loadingMoreRef.current = true;
    setIsLoadingMore(true);
    fetchFansList({
      from: range[0],
      to: range[1],
      currency,
      sort,
      order,
      startFrom: nextFrom,
      limit: PAGE_SIZE,
      promoLinkIds,
    })
      .then((res) => {
        dispatch(mainActions.setUsers(res.users));
        setFans((prev) => [...prev, ...res.fans]);
        setNextFrom(res.nextFrom);
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e?.message || 'Failed to load fans'));
      })
      .finally(() => {
        loadingMoreRef.current = false;
        setIsLoadingMore(false);
      });
  }, [nextFrom, range, currency, sort, order, promoLinkIds, dispatch]);

  const handlePresetChange = useCallback(
    (next: DateRangePreset, startDay: number, endDay: number) => {
      setPreset(next);
      setRange(presetToRange(next, startDay, endDay));
    },
    [],
  );

  const ccy = currency.toUpperCase();

  const renderFan = (fan: FanRowData, index: number) => {
    const amountLabel = `${formatAmount(fan.totalAmount, currency)} ${ccy}`;
    const countLabel = t('statistics_pro_fans_operations', {
      count: fan.totalCount,
      defaultValue: '%{count} operations',
    });
    const rank = order === 'asc' && summary ? summary.fanCount - index : index + 1;
    return (
      <FanRow
        key={fan.userId}
        rank={rank}
        active={fan.userId === activeUserId}
        userId={fan.userId}
        primary={sort === 'count' ? String(fan.totalCount) : amountLabel}
        secondary={sort === 'count' ? amountLabel : countLabel}
        topType={topTypeOf(fan.breakdown, sort)}
        onClick={(centerRatio) => {
          focusAt(centerRatio);
          setActiveUserId(fan.userId);
          setSelectedFanId(fan.userId);
        }}
      />
    );
  };

  const isEmpty = loadedOnce && !isLoading && fans.length === 0;

  return (
    <>
      <MetaTags title={t('statistics_pro_fans_title', { defaultValue: 'Top fans' })} />
      <Header
        showBackButton
        rightIcons={[{ icon: <IcFilters28 />, onClick: () => setSortOpen(true) }]}
      >
        {t('statistics_pro_fans_title', { defaultValue: 'Top fans' })}
      </Header>

      <div className="StatisticsProControls">
        <CurrencyButton value={currency} onChange={setCurrency} />
        <DateButton
          preset={preset}
          startDay={range[0]}
          endDay={range[1]}
          onChange={handlePresetChange}
        />
      </div>

      {!hasError &&
        (isLoading ? (
          <FansSummarySkeleton />
        ) : summary ? (
          <FansSummary summary={summary} currency={currency} />
        ) : null)}

      {isLoading ? (
        <FanRowSkeleton />
      ) : hasError ? (
        <Placeholder
          title={t('failed_title', { defaultValue: 'Something went wrong' })}
          actions={
            <Button size="small" onClick={() => setReloadKey((k) => k + 1)}>
              {t('failed_button', { defaultValue: 'Try again' })}
            </Button>
          }
        >
          {t('statistics_pro_fans_error', {
            defaultValue: "Couldn't load top fans. Please try again.",
          })}
        </Placeholder>
      ) : isEmpty ? (
        <Placeholder>
          {t('statistics_pro_fans_empty', {
            defaultValue: 'No paying fans in this period.',
          })}
        </Placeholder>
      ) : (
        <ScrollView
          className="StatisticsProFans__list"
          canLoadMore={nextFrom !== null && !isLoadingMore}
          onLoadMore={handleLoadMore}
          isLoadingMore={isLoadingMore}
        >
          {fans.map(renderFan)}
        </ScrollView>
      )}

      {sortOpen && (
        <FansSortModal
          sort={sort}
          order={order}
          preset={preset}
          startDay={range[0]}
          endDay={range[1]}
          links={promoLinkIds}
          onChangeSort={setSort}
          onChangeOrder={setOrder}
          onChangeRange={handlePresetChange}
          onChangeLinks={setPromoLinkIds}
          onClose={() => setSortOpen(false)}
        />
      )}

      {selectedFanId !== null && (
        <FanDetailModal
          userId={selectedFanId}
          currency={currency}
          range={range}
          onClose={() => setSelectedFanId(null)}
        />
      )}
    </>
  );
};
